import * as React from 'react';
import { cn } from '@noor/lib/cn';

/**
 * The five Eats bottom-nav glyphs, exported from the Noor.Merchant Figma mobile
 * layout (node 193:5718) and cleaned to `currentColor` fills so each inherits
 * its color from the `BottomNav.Item` state (brand orange when active, muted
 * gray otherwise). Solid marks, 24×24 source. Use like a lucide icon:
 * `<NavDashboardIcon className="size-6" />`.
 */
type IconProps = React.ComponentProps<'svg'>;

/** Dashboard tab — a rounded-square bar chart (solid). */
function NavDashboardIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2 7C2 4.23858 4.23858 2 7 2H17C19.7614 2 22 4.23858 22 7V17C22 19.7614 19.7614 22 17 22H7C4.23858 22 2 19.7614 2 17V7ZM9 10C9 9.44772 8.55228 9 8 9C7.44772 9 7 9.44772 7 10V16C7 16.5523 7.44772 17 8 17C8.55228 17 9 16.5523 9 16V10ZM12 7C12.5523 7 13 7.44772 13 8V16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16V8C11 7.44772 11.4477 7 12 7ZM17 13C17 12.4477 16.5523 12 16 12C15.4477 12 15 12.4477 15 13V16C15 16.5523 15.4477 17 16 17C16.5523 17 17 16.5523 17 16V13Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Orders tab — a room-service cloche on a table line (solid). */
function NavOrdersIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14 6C14 6.08402 13.9948 6.16683 13.9848 6.24813C17.4429 7.1309 20 10.2668 20 14V15C20 15.5523 19.5523 16 19 16H5C4.44772 16 4 15.5523 4 15V14C4 10.2668 6.55706 7.1309 10.0152 6.24813C10.0052 6.16683 10 6.08402 10 6C10 4.89543 10.8954 4 12 4C13.1046 4 14 4.89543 14 6Z"
        fill="currentColor"
      />
      <path
        d="M3 18C2.44772 18 2 18.4477 2 19C2 19.5523 2.44772 20 3 20H21C21.5523 20 22 19.5523 22 19C22 18.4477 21.5523 18 21 18H3Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Menu tab — a document / folded-corner doc (solid). */
function NavMenuIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4 7C4 4.23858 6.23858 2 9 2H12.5969C13.7322 2 14.8338 2.3864 15.7204 3.09566L18.1235 5.01815C19.3096 5.96702 20 7.40358 20 8.9225V17C20 19.7614 17.7614 22 15 22H9C6.23858 22 4 19.7614 4 17V7ZM13 6V4L18 8H15C13.8954 8 13 7.10457 13 6ZM8 13C8 12.4477 8.44772 12 9 12H15C15.5523 12 16 12.4477 16 13C16 13.5523 15.5523 14 15 14H9C8.44772 14 8 13.5523 8 13ZM9 16C8.44772 16 8 16.4477 8 17C8 17.5523 8.44772 18 9 18H15C15.5523 18 16 17.5523 16 17C16 16.4477 15.5523 16 15 16H9Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Venue tab — a market stall / shopfront with awning (solid). */
function NavVenueIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M22 8.32297C22 9.56756 21.1507 10.6138 20 10.9137V20H21C21.5523 20 22 20.4477 22 21C22 21.5523 21.5523 22 21 22H3C2.44772 22 2 21.5523 2 21C2 20.4477 2.44772 20 3 20H4V10.9136C2.84932 10.6138 2 9.56756 2 8.32297C2 7.9824 2.06499 7.64495 2.19147 7.32874L3.06575 5.14305C3.82507 3.24475 5.66361 2 7.70813 2H16.2919C18.3364 2 20.1749 3.24476 20.9343 5.14305L21.8085 7.32874C21.935 7.64495 22 7.9824 22 8.32297ZM4.9227 5.88583C5.25486 5.05543 5.93121 4.43347 6.74871 4.15741L6.01942 7.80388L6.02507 7.80501C6.01638 7.86977 6.00994 7.9348 6.00576 8H6C6 8.03075 6.00046 8.0614 6.00138 8.09193C6.00046 8.12134 6 8.15076 6 8.1802C6 8.63296 5.63296 9 5.1802 9H4.67703C4.30312 9 4 8.69688 4 8.32297C4 8.23684 4.01644 8.15149 4.04842 8.07152L4.9227 5.88583ZM10 9H9C8.47973 9 8.05226 8.60269 8.00445 8.09493C8.00709 8.06961 8.01092 8.04441 8.01592 8.01942L8.8198 4H11V8C11 8.55228 10.5523 9 10 9ZM15 9H14C13.4477 9 13 8.55228 13 8V4H15.1802L15.9967 8.08232C15.9548 8.5961 15.5246 9 15 9ZM18.9057 9C18.4055 9 18 8.59453 18 8.09435C18 8.08031 17.9999 8.06628 17.9996 8.05226C17.9999 8.03487 18 8.01745 18 8H17.9977C17.9946 7.93449 17.9881 7.86919 17.9784 7.80433L17.9806 7.80388L17.9631 7.7167L17.9621 7.71137L17.2513 4.15741C18.0688 4.43347 18.7451 5.05543 19.0773 5.88583L19.9516 8.07152C19.9836 8.15149 20 8.23684 20 8.32297C20 8.69688 19.6969 9 19.323 9H18.9057ZM9 20H15V17C15 15.3431 13.6569 14 12 14C10.3431 14 9 15.3431 9 17V20Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Users tab — two overlapping people (solid). */
function NavUsersIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.50708 7.34328C4.50708 4.94455 6.45163 3 8.85036 3C11.2491 3 13.1936 4.94455 13.1936 7.34328C13.1936 9.74201 11.2491 11.6866 8.85036 11.6866C6.45163 11.6866 4.50708 9.74201 4.50708 7.34328Z"
        fill="currentColor"
      />
      <path
        d="M16.3553 10.9778C15.6375 10.9778 14.9704 10.761 14.4157 10.3894C14.9116 9.4853 15.1936 8.44722 15.1936 7.34328C15.1936 6.32704 14.9546 5.36661 14.5298 4.51512C15.0609 4.18843 15.6861 4 16.3553 4C18.2822 4 19.8442 5.56203 19.8442 7.4889C19.8442 9.41577 18.2822 10.9778 16.3553 10.9778Z"
        fill="currentColor"
      />
      <path
        d="M14.1233 13.1171C16.0484 14.312 17.397 16.3477 17.6561 18.7111H20.1778C21.0246 18.7111 21.7111 18.0246 21.7111 17.1778C21.7111 14.8582 19.8307 12.9778 17.5111 12.9778H15.2C14.8278 12.9778 14.4669 13.0262 14.1233 13.1171Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.14286 13C4.75025 13 2 15.7503 2 19.1429C2 20.1685 2.83147 21 3.85714 21H14.1429C15.1685 21 16 20.1685 16 19.1429C16 15.7503 13.2498 13 9.85714 13H8.14286Z"
        fill="currentColor"
      />
    </svg>
  );
}

export { NavDashboardIcon, NavOrdersIcon, NavMenuIcon, NavVenueIcon, NavUsersIcon };
export type { IconProps as EatsNavIconProps };
